import React, { FC } from 'react';
import { InputText } from '@/components';
import { TypeInputFreeformDateFields } from '@/core/generated-types';
import { freeformDateFormatHelper } from '@/utils/helpers';
import { ToolTip } from '@/components/ui/ToolTip';

enum DateFragment {
  year = 'year',
  month = 'month',
  day = 'day',
}

interface InputDateFragmentProps {
  id: string;
  fragmentName: DateFragment;
  placeholder: string;
  value: string;
  pattern: string;
  maxLength: number;
  lastField: boolean;
  error: boolean;
  disabled: boolean;
  ignorePasskeys?: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    fragmentName: DateFragment,
    id: string,
  ) => void;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement>,
    fragmentName: DateFragment,
    id: string,
  ) => void;
}

const InputDateFragment: FC<InputDateFragmentProps> = ({
  id,
  fragmentName,
  lastField = false,
  onChange,
  onBlur,
  disabled,
  ignorePasskeys,
  ...props
}) => {
  // We intercept events to remove non-number input instead of using type=number because we're using the pattern HTML attribute to validate the date fragments, which doesn't work with type=number
  const interceptOnChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fragmentName: DateFragment,
    id: string,
  ) => {
    event.target.value = event.target.value.replace(/[^0-9]+/g, '');
    onChange(event, fragmentName, id);
  };

  const interceptOnBlur = (
    event: React.ChangeEvent<HTMLInputElement>,
    fragmentName: DateFragment,
    id: string,
  ) => {
    if (onBlur) {
      event.target.value = event.target.value.replace(/[^0-9]+/g, '');
      onBlur(event, fragmentName, id);
    }
  };

  return (
    <InputText
      id={id}
      className={lastField ? '' : 'sm:pr-2'}
      disabled={disabled}
      ignorePasskeys={ignorePasskeys}
      onChange={(event) => interceptOnChange(event, fragmentName, id)}
      onBlur={(event) => interceptOnBlur(event, fragmentName, id)}
      type="phone"
      {...props}
    />
  );
};

interface InputFreeformDateProps extends TypeInputFreeformDateFields {
  value: { year: string; month: string; day: string };
  error: string;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    type: DateFragment,
    id: string,
  ) => void;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement>,
    type: DateFragment,
    id: string,
  ) => void;
  disabled: boolean;
  shouldDisplayTooltip: boolean;
  ignorePasskeys?: boolean;
}

const InputFreeformDate: FC<InputFreeformDateProps> = ({
  label,
  id,
  yearPlaceholder,
  monthPlaceholder,
  dayPlaceholder,
  dateFormat,
  value,
  error,
  onChange,
  onBlur,
  disabled = false,
  toolTipContent,
  shouldDisplayTooltip = false,
  ignorePasskeys = false,
}) => {
  const dateFragments = freeformDateFormatHelper(dateFormat) as DateFragment[];

  const freeformDateConfiguration = {
    [DateFragment.year]: {
      placeholder: yearPlaceholder,
      // Expects a year following the xyzw pattern where x is 1-2, y is 0-9, z is 0-9, and w is 0-9.
      pattern: /(1|2)[0-9][0-9][0-9]/,
      maxLength: 4,
    },
    [DateFragment.month]: {
      placeholder: monthPlaceholder,
      // Expects a month following the x or xy pattern where x is 0 or 1 and y is 1-9 or 0-2
      pattern: /0?[1-9]|1[012]/,
      maxLength: 2,
    },
    [DateFragment.day]: {
      placeholder: dayPlaceholder,
      // Expects a day following the x or xy pattern where x is 0, 1-2, or 3, and y is 1-9, 0-9, or 0-1.
      pattern: /0?[1-9]|[12][0-9]|3[01]/,
      maxLength: 2,
    },
  };

  return (
    <div>
      <div className="flex flex-row">
        <label className={`text-sm font-bold ${error && 'text-warning'}`}>
          {label}
        </label>
        {shouldDisplayTooltip && toolTipContent && (
          <ToolTip
            id={`tooltip-${id}`}
            height={17}
            width={17}
            content={toolTipContent}
            classes="ml-1"
            strokeWidth={2.5}
          />
        )}
      </div>
      <div
        className="container grid sm:grid-cols-3"
        data-testid="freeform-date-inputs"
      >
        {dateFragments.map((dateFragment, index) => {
          const dateFragmentConfiguration =
            freeformDateConfiguration[dateFragment];
          return (
            <InputDateFragment
              key={dateFragment}
              fragmentName={dateFragment}
              id={`${id}-${dateFragment}`}
              placeholder={dateFragmentConfiguration.placeholder || ''}
              pattern={dateFragmentConfiguration.pattern
                .toString()
                .slice(1, -1)}
              maxLength={dateFragmentConfiguration.maxLength}
              lastField={index === dateFragments.length - 1}
              value={value[dateFragment]}
              error={!!error}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              ignorePasskeys={ignorePasskeys}
            />
          );
        })}
      </div>
      <div className="text-sm">
        {error && <span className="text-warning">{error}</span>}
      </div>
    </div>
  );
};

export default React.memo(InputFreeformDate);
